import { Box, Grid, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useApiQuery } from "../../../../../../../core/api/useApiQuery"
import { UserWeekMenu, fetchUserPos, FetchUserPosResponse } from "../../../weekMenuApi"
import { useAppSelector } from "../../../../../../store/storeHooks"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { isEmpty } from "lodash"
import { PurchasesCard } from "./PurchasesCard"
import React from "react"
import { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUtensils } from "@fortawesome/pro-solid-svg-icons"
import { selectUserId } from "../../../../../../login/loginSelectors"
import { ErrorBoundary } from "react-error-boundary"
import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { QueryErrorBoundary } from "../../../../../../../core/api/components/QueryErrorBoundary"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    column: {
        paddingTop: "2rem !important",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
    noDataContainer: {
        padding: "1.5rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        background: theme.palette.common.white,
        borderRadius: ".625rem",
        textAlign: "center",
        boxShadow: "0px 2px 6px 0px #00000014",
    },
    noMeetingIcon: {
        fontSize: "4.5rem",
        color: theme.palette.tailwind.green[900],
        backgroundColor: theme.palette.tailwind.green[200],
        borderRadius: ".5rem",
        padding: ".75rem",
        marginBottom: "1rem",
    },
    skeletonCard: {
        height: "12rem",
        borderRadius: ".625rem",
    },
}))

const transform = (data: FetchUserPosResponse) => {
    return data.orders
}

function SafeUserPurchases() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectUserId)
    const startDate = DateTime.now().startOf("week").toISODate()
    const endDate = DateTime.now().endOf("week").toISODate()

    const { data: purchases, isLoading } = useApiQuery(fetchUserPos, {
        queryName: UserWeekMenu.USER_POS_PURCHASES,
        dependencies: {
            userId: userId ?? 0,
            view: "dashboard",
        },
        enabled: Boolean(userId),
        useErrorBoundary: true,
        select: transform,
    })

    return (
        <Grid item xs={12} md={6} className={classes.column}>
            <LocalizedStrict id="user-home-upcoming-purchases">
                <Typography variant="h6" fontWeight="bold">
                    Purchases
                </Typography>
            </LocalizedStrict>
            {isLoading ? (
                <>
                    <Skeleton variant="rectangular" className={classes.skeletonCard} />
                    <Skeleton variant="rectangular" className={classes.skeletonCard} />
                </>
            ) : !isEmpty(purchases) ? (
                <ErrorBoundary
                    fallback={
                        <UserDashboardErrorFallbackCard
                            icon={<FontAwesomeIcon icon={faUtensils} className={classes.noMeetingIcon} />}
                            errorMessage={l10n.getString("user-home-upcoming-purchases-load-error")}
                        />
                    }
                >
                    {purchases?.map((purchase) => (
                        <PurchasesCard key={purchase.id} purchase={purchase} purchases={purchases} />
                    ))}
                </ErrorBoundary>
            ) : (
                <NoDataCard />
            )}
        </Grid>
    )
}

export function UserPurchases() {
    return (
        <QueryErrorBoundary Fallback={NoDataFallback}>
            <SafeUserPurchases />
        </QueryErrorBoundary>
    )
}

function NoDataFallback() {
    const { classes } = useStyles()
    return (
        <Grid item xs={12} md={6} className={classes.column}>
            <LocalizedStrict id="user-home-upcoming-purchases">
                <Typography variant="h6" fontWeight="bold">
                    Purchases
                </Typography>
            </LocalizedStrict>
            <NoDataCard />
        </Grid>
    )
}

function NoDataCard() {
    const { classes } = useStyles()
    return (
        <Box className={classes.noDataContainer}>
            <FontAwesomeIcon icon={faUtensils} className={classes.noMeetingIcon} />
            <LocalizedStrict id="user-home-upcoming-purchases-no-purchases">
                <Typography color="#00000099" variant="subtitle1">
                    No upcoming purchases
                </Typography>
            </LocalizedStrict>
        </Box>
    )
}
